import {Inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {CoreState, isUserAuthenticated} from '../../store';
import {LogoutAction, RedirectedUrlAction} from '../../store/user';
import {Employee} from '../../models/employee/employee.model';
import {OktaAuth} from '@okta/okta-auth-js';
import {OKTA_AUTH} from '@okta/okta-angular';
import {Router} from '@angular/router';
import {ResetStandaloneStore} from '../../store/standalone';
import {ClearTranslationsAction} from '../../store/translation';
import {EppSynchronizerService} from '../../rxdb/synchronizer/epp.synchronizer.service';
import {RxdbRepository} from '../../rxdb/rxdb.repository';
import {StandaloneModeService} from '../../standalone-mode.service';
import {clearPullMarks} from '../../rxdb/synchronizer/multi-tab-sync';

export const AUTH_CHANNEL_NAME = 'epp-auth';
export const LOGOUT_MESSAGE = 'LOGOUT';
const FLUSH_TIMEOUT_MS = 10_000;
const DELETE_DB_TIMEOUT_MS = 5_000;

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // private fields
    private unsubscribe: Subscription[] = [];
    private authChannel: BroadcastChannel;
    private loggingOut = false;

    // public fields
    currentUser$: Observable<Employee>;
    isLoading$: Observable<boolean>;
    currentUserSubject: BehaviorSubject<Employee>;
    isLoadingSubject: BehaviorSubject<boolean>;

    set currentUserValue(user: Employee) {
        this.currentUserSubject.next(user);
    }

    // private fields
    constructor(
        private store: Store<CoreState>,
        private router: Router,
        private eppSynchronizerService: EppSynchronizerService,
        private rxdbRepository: RxdbRepository,
        private standaloneModeService: StandaloneModeService,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth
    ) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.isLoading$ = this.isLoadingSubject.asObservable();
        // un logout dans un onglet déconnecte tous les autres onglets
        if (typeof BroadcastChannel !== 'undefined') {
            this.authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
            this.authChannel.onmessage = event => {
                if (event.data?.type === LOGOUT_MESSAGE) {
                    this.handleRemoteLogout();
                }
            };
        }
    }

    public authenticated(): Observable<boolean> {
        return this.store.pipe(select(isUserAuthenticated));
    }

    public async logout(): Promise<void> {
        if (this.loggingOut) {
            return;
        }
        this.loggingOut = true;
        try {
            const isStandaloneActive = localStorage.getItem('isStandaloneActive') === 'true';
            if (isStandaloneActive) {
                // les données offline de tous les onglets sont dans l'IndexedDB partagée : on les pousse avant de la supprimer
                await withTimeout(this.eppSynchronizerService.flushPendingPushes(), FLUSH_TIMEOUT_MS)
                    .catch(err => console.error('Error while pushing pending data before logout:', err));
            }
            await this.stopLocalSync();
            this.authChannel?.postMessage({type: LOGOUT_MESSAGE});

            const databases = await indexedDB.databases?.() ?? [];
            await Promise.all(databases.filter(db => !!db.name).map(db => deleteDatabase(db.name!)));
            localStorage.removeItem('isStandaloneActive');
            clearPullMarks();
            this.resetStore();
            await this._oktaAuth.signOut();
        } finally {
            this.loggingOut = false;
        }
    }

    /**
     * Logout déclenché depuis un autre onglet : cet onglet libère IndexedDB et revient sur l'écran de connexion.
     * La redirection Okta (signOut) et la suppression des bases sont faites par l'onglet à l'origine du logout.
     */
    public async handleRemoteLogout(): Promise<void> {
        if (this.loggingOut) {
            return;
        }
        this.loggingOut = true;
        try {
            await this.stopLocalSync();
            // indispensable : sinon ngrx-store-localstorage de cet onglet réécrirait user/standalone dans le localStorage
            this.resetStore();
            this._oktaAuth.tokenManager.clear();
            await this.router.navigate(['/auth']);
        } finally {
            this.loggingOut = false;
        }
    }

    private async stopLocalSync(): Promise<void> {
        this.eppSynchronizerService.stop();
        this.standaloneModeService.reset();
        await this.rxdbRepository.close()
            .catch(err => console.error('Error while closing RxDB:', err));
    }

    private resetStore(): void {
        this.store.dispatch(new LogoutAction());
        this.store.dispatch(new ResetStandaloneStore());
        this.store.dispatch(new ClearTranslationsAction());
    }

    public login(): void {
        this._oktaAuth.closeSession().then(() => this.router.navigate(['/']));
    }

    public storeRedirectedUrl(url: string) {
        this.store.dispatch(new RedirectedUrlAction({redirectedUrl: url}));
    }

    ngOnDestroy() {
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
        this.authChannel?.close();
    }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | void> {
    return Promise.race([promise, new Promise<void>(resolve => setTimeout(resolve, timeoutMs))]);
}

/**
 * Supprime une base IndexedDB sans bloquer indéfiniment si un autre onglet la garde ouverte.
 */
function deleteDatabase(name: string): Promise<void> {
    return withTimeout(new Promise<void>(resolve => {
        const request = indexedDB.deleteDatabase(name);
        request.onsuccess = () => resolve();
        request.onerror = () => {
            console.warn(`Could not delete IndexedDB database ${name}`, request.error);
            resolve();
        };
        request.onblocked = () => console.warn(`Deletion of IndexedDB database ${name} is waiting for other tabs to close it`);
    }), DELETE_DB_TIMEOUT_MS) as Promise<void>;
}
